import { Extension } from "@tiptap/core";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";

// Marks checklist items whose text wraps onto more than one line with an
// `is-wrapped` class, so CSS can draw a separator underneath them. Without it a
// wrapped item runs straight into the next item's checkbox with no visual break.
//
// This MUST be done with a ProseMirror Decoration rather than by toggling the
// class on the <li> directly: ProseMirror reconciles the DOM it owns, treats an
// externally-added class as damage, and re-renders the node to strip it — which
// re-triggers any observer that added it, producing an infinite render loop.
// Decorations are applied by ProseMirror itself, so they survive re-renders.

const checklistWrapKey = new PluginKey("checklistWrapDecoration");

function isWrappedContent(content) {
    const style = window.getComputedStyle(content);
    let lineHeight = parseFloat(style.lineHeight);
    if (!lineHeight || Number.isNaN(lineHeight)) {
        lineHeight = parseFloat(style.fontSize || "16") * 1.4;
    }
    // 1.4x rather than 2x: catches a second line without false-positiving on
    // the sub-pixel slack a single line can pick up from descenders/inline nodes.
    return content.scrollHeight > lineHeight * 1.4;
}

function findWrappedPositions(view) {
    const positions = [];
    view.state.doc.descendants((node, pos) => {
        if (node.type.name !== "taskItem") return;
        const dom = view.nodeDOM(pos);
        if (!dom || dom.nodeType !== 1) return;
        const content = dom.querySelector(":scope > div, :scope > p");
        if (!content) return;
        if (isWrappedContent(content)) positions.push(pos);
    });
    return positions;
}

export const ChecklistWrapDecoration = Extension.create({
    name: "checklistWrapDecoration",

    addProseMirrorPlugins() {
        return [
            new Plugin({
                key: checklistWrapKey,
                state: {
                    init: () => DecorationSet.empty,
                    apply(tr, previous) {
                        const meta = tr.getMeta(checklistWrapKey);
                        if (meta) return meta.decorations;
                        return previous.map(tr.mapping, tr.doc);
                    }
                },
                props: {
                    decorations(state) {
                        return checklistWrapKey.getState(state);
                    }
                },
                view(editorView) {
                    let frame = 0;
                    // Signature of the last applied set. Re-dispatching only when
                    // it actually changes is what stops the measure -> dispatch ->
                    // update -> measure cycle from running forever.
                    let lastSignature = null;

                    const sync = () => {
                        if (frame) return;
                        frame = window.requestAnimationFrame(() => {
                            frame = 0;
                            if (!editorView.dom || !editorView.dom.isConnected) return;

                            const positions = findWrappedPositions(editorView);
                            const signature = positions.join(",");
                            if (signature === lastSignature) return;
                            lastSignature = signature;

                            const decorations = [];
                            positions.forEach((pos) => {
                                const node = editorView.state.doc.nodeAt(pos);
                                if (!node) return;
                                decorations.push(
                                    Decoration.node(pos, pos + node.nodeSize, { class: "is-wrapped" })
                                );
                            });

                            editorView.dispatch(
                                editorView.state.tr
                                    .setMeta(checklistWrapKey, {
                                        decorations: DecorationSet.create(editorView.state.doc, decorations)
                                    })
                                    // Purely presentational: must not land on the undo stack.
                                    .setMeta("addToHistory", false)
                            );
                        });
                    };

                    // Re-measure when the editor box changes width (modal resize,
                    // sidebar collapse, device rotation) — wrapping depends on it.
                    const resizeObserver = typeof ResizeObserver !== "undefined"
                        ? new ResizeObserver(sync)
                        : null;
                    if (resizeObserver) resizeObserver.observe(editorView.dom);

                    sync();

                    return {
                        update: sync,
                        destroy() {
                            if (frame) window.cancelAnimationFrame(frame);
                            if (resizeObserver) resizeObserver.disconnect();
                        }
                    };
                }
            })
        ];
    }
});

export default ChecklistWrapDecoration;
